import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { formatChartValue } from '../../utils/chartFormatters';

/**
 * Income vs Expenses Over Time Chart
 * Shows how income sources change throughout retirement years
 * Highlights when state pension/social security kicks in and when other income sources start/end
 */
export default function IncomeVsExpensesChart({
  drawdownData = null, // NEW: Full drawdown projections
  desiredIncome,
  essentialSpending,
  discretionarySpending,
  currencySymbol = "$",
  retirementAge = 65,
  statePensionAge = 67,
  country = 'US'
}) {
  // If no drawdown data provided, show nothing (or fallback message)
  if (!drawdownData || !drawdownData.projections || drawdownData.projections.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Income vs Expenses Over Time</CardTitle>
          <CardDescription>
            Run calculations to see how your income sources change throughout retirement
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-96 flex items-center justify-center text-muted-foreground">
            <p>No retirement projection data available. Please run calculations.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Labels based on country
  const statePensionLabel = country === 'UK' ? 'Your State Pension' : 'Your Social Security';
  const spouseStatePensionLabel = country === 'UK' ? 'Spouse State Pension' : 'Spouse Social Security';

  // Show first 16 years of retirement (typically covers when income composition changes most)
  const yearsToShow = Math.min(16, drawdownData.projections.length);
  const projections = drawdownData.projections.slice(0, yearsToShow);

  // Transform data for stacked bar chart
  const chartData = projections.map((yearData) => {
    const age = yearData.age;

    // Separate main person's state pension from spouse's state pension
    const mainStatePension = yearData.statePension || 0;

    // Calculate spouse state pension (if it's included in spouseIncome)
    // We need to extract it from the total spouse income
    const spouseStatePension = yearData.spouseStatePension || 0;
    const spouseOtherIncome = Math.max(0, (yearData.spouseIncome || 0) - spouseStatePension);

    // For phased UK tax-free lump sums: the tax-free portion is PART OF the withdrawal
    // Split withdrawal into taxable and tax-free components for correct visualization
    const withdrawal = yearData.withdrawal || 0;
    const taxFreeLumpSum = yearData.taxFreeLumpSum || 0;
    const isPhased = !yearData.isLumpSumAdditionalIncome && taxFreeLumpSum > 0;

    return {
      age: age,
      year: `Age ${age}`,
      // Income sources (in order they should appear in stack)
      // For phased withdrawals: show taxable portion separately from tax-free portion
      'Portfolio Withdrawal (Taxable)': isPhased ? Math.max(0, withdrawal - taxFreeLumpSum) : withdrawal,
      'Tax-Free Lump Sum': taxFreeLumpSum,
      [statePensionLabel]: mainStatePension,
      [spouseStatePensionLabel]: spouseStatePension,
      'Rental Income': yearData.rentalIncome || 0,
      'Part-Time Work': yearData.partTimeIncome || 0,
      'Annuity Income': yearData.annuityIncome || 0,
      'Other Income': yearData.otherPassiveIncome || 0,
      'Spouse Other Income': spouseOtherIncome,
      // Total income and expenses
      totalIncome: yearData.totalIncome || 0,
      totalExpenses: (yearData.expenses?.essential || 0) + (yearData.expenses?.discretionary || 0),
    };
  });

  // Determine which income sources exist in the data
  const hasMainStatePension = chartData.some(d => d[statePensionLabel] > 0);
  const hasSpouseStatePension = chartData.some(d => d[spouseStatePensionLabel] > 0);
  const hasTaxFreeLumpSum = chartData.some(d => d['Tax-Free Lump Sum'] > 0);
  const hasRentalIncome = chartData.some(d => d['Rental Income'] > 0);
  const hasPartTimeWork = chartData.some(d => d['Part-Time Work'] > 0);
  const hasAnnuityIncome = chartData.some(d => d['Annuity Income'] > 0);
  const hasOtherIncome = chartData.some(d => d['Other Income'] > 0);
  const hasSpouseOtherIncome = chartData.some(d => d['Spouse Other Income'] > 0);

  // Find when state pension kicks in
  const statePensionStartYear = chartData.findIndex(d => d[statePensionLabel] > 0);
  const spouseStatePensionStartYear = chartData.findIndex(d => d[spouseStatePensionLabel] > 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div className="bg-white p-4 border border-gray-200 rounded shadow-lg max-w-xs">
          <p className="font-semibold mb-2">{label}</p>

          {/* Income sources */}
          <div className="space-y-1 mb-3">
            <p className="text-xs font-semibold text-gray-600 mb-1">Income Sources:</p>
            {payload.filter(p => p.value > 0 && p.dataKey !== 'totalExpenses' && p.dataKey !== 'totalIncome').map((entry, index) => (
              <div key={index} className="flex justify-between gap-4 text-sm">
                <span style={{ color: entry.color }}>• {entry.name}:</span>
                <span className="font-medium">
                  {currencySymbol}{entry.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t pt-2 space-y-1">
            <div className="flex justify-between gap-4 text-sm">
              <span className="font-semibold">Total Income:</span>
              <span className="font-bold text-green-600">
                {currencySymbol}{data.totalIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between gap-4 text-sm">
              <span className="font-semibold">Total Expenses:</span>
              <span className="font-bold text-orange-600">
                {currencySymbol}{data.totalExpenses.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between gap-4 text-sm pt-1 border-t">
              <span className="font-semibold">Net:</span>
              <span className={`font-bold ${data.totalIncome >= data.totalExpenses ? 'text-green-600' : 'text-red-600'}`}>
                {data.totalIncome >= data.totalExpenses ? '+' : ''}{currencySymbol}{(data.totalIncome - data.totalExpenses).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Income vs Expenses Over Time</CardTitle>
        <CardDescription>
          How your income sources evolve throughout retirement years
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Key insights */}
        <div className="mb-4 space-y-2">
          {hasMainStatePension && statePensionStartYear >= 0 && (
            <div className="flex items-center gap-2 text-sm p-2 bg-purple-50 rounded border border-purple-200">
              <span className="text-purple-600 font-semibold">ℹ</span>
              <span className="text-purple-900">
                {statePensionLabel} begins at age {chartData[statePensionStartYear]?.age}
                ({currencySymbol}{chartData.find(d => d[statePensionLabel] > 0)?.[statePensionLabel].toLocaleString()}/year)
              </span>
            </div>
          )}
          {hasSpouseStatePension && spouseStatePensionStartYear >= 0 && (
            <div className="flex items-center gap-2 text-sm p-2 bg-purple-50 rounded border border-purple-200">
              <span className="text-purple-600 font-semibold">ℹ</span>
              <span className="text-purple-900">
                {spouseStatePensionLabel} begins at age {chartData[spouseStatePensionStartYear]?.age}
                ({currencySymbol}{chartData.find(d => d[spouseStatePensionLabel] > 0)?.[spouseStatePensionLabel].toLocaleString()}/year)
              </span>
            </div>
          )}
          {hasTaxFreeLumpSum && (
            <div className="flex items-center gap-2 text-sm p-2 bg-amber-50 rounded border border-amber-200">
              <span className="text-amber-600 font-semibold">ℹ</span>
              <span className="text-amber-900">
                {country === 'UK' ? '25% tax-free lump sum withdrawals shown in yellow' : 'Tax-free withdrawals shown in yellow'}
              </span>
            </div>
          )}
        </div>

        {/* Main Chart */}
        <div className="h-[500px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={(value) => formatChartValue(value, currencySymbol)}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="rect"
              />

              {/* Income sources as stacked bars */}
              <Bar dataKey="Portfolio Withdrawal (Taxable)" stackId="income" fill="#3b82f6" name="Portfolio Withdrawal (Taxable)" />

              {hasMainStatePension && (
                <Bar dataKey={statePensionLabel} stackId="income" fill="#8b5cf6" name={statePensionLabel} />
              )}

              {hasSpouseStatePension && (
                <Bar dataKey={spouseStatePensionLabel} stackId="income" fill="#a78bfa" name={spouseStatePensionLabel} />
              )}

              {hasTaxFreeLumpSum && (
                <Bar dataKey="Tax-Free Lump Sum" stackId="income" fill="#eab308" name="Tax-Free Lump Sum" />
              )}

              {hasRentalIncome && (
                <Bar dataKey="Rental Income" stackId="income" fill="#f97316" name="Rental Income" />
              )}

              {hasPartTimeWork && (
                <Bar dataKey="Part-Time Work" stackId="income" fill="#14b8a6" name="Part-Time Work" />
              )}

              {hasAnnuityIncome && (
                <Bar dataKey="Annuity Income" stackId="income" fill="#6366f1" name="Annuity Income" />
              )}

              {hasOtherIncome && (
                <Bar dataKey="Other Income" stackId="income" fill="#ec4899" name="Other Income" />
              )}

              {hasSpouseOtherIncome && (
                <Bar dataKey="Spouse Other Income" stackId="income" fill="#f43f5e" name="Spouse Other Income" />
              )}

              {/* Total expenses as a line */}
              <Line
                type="monotone"
                dataKey="totalExpenses"
                stroke="#f59e0b"
                strokeWidth={3}
                dot={{ fill: '#f59e0b', r: 4 }}
                name="Total Expenses"
                strokeDasharray="5 5"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Summary Statistics */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* First year summary */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-xs font-semibold text-blue-600 mb-1">First Year (Age {chartData[0]?.age})</div>
            <div className="text-2xl font-bold text-blue-900">
              {currencySymbol}{chartData[0]?.totalIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div className="text-xs text-blue-700 mt-1">Total Income</div>
          </div>

          {/* When state pension starts */}
          {hasMainStatePension && statePensionStartYear >= 0 && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-xs font-semibold text-purple-600 mb-1">
                When {statePensionLabel} Starts (Age {chartData[statePensionStartYear]?.age})
              </div>
              <div className="text-2xl font-bold text-purple-900">
                {currencySymbol}{chartData[statePensionStartYear]?.totalIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div className="text-xs text-purple-700 mt-1">
                Portfolio withdrawal reduces by {currencySymbol}{chartData.find(d => d[statePensionLabel] > 0)?.[statePensionLabel].toLocaleString()}
              </div>
            </div>
          )}

          {/* Average over period */}
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="text-xs font-semibold text-green-600 mb-1">
              Average ({yearsToShow} years shown)
            </div>
            <div className="text-2xl font-bold text-green-900">
              {currencySymbol}{Math.round(chartData.reduce((sum, d) => sum + d.totalIncome, 0) / chartData.length).toLocaleString()}
            </div>
            <div className="text-xs text-green-700 mt-1">Average Annual Income</div>
          </div>
        </div>

        {/* Income source breakdown */}
        <div className="mt-6 pt-4 border-t">
          <h4 className="text-sm font-semibold mb-3">Income Sources Timeline</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span className="flex-1">Portfolio Withdrawal (Taxable)</span>
              <span className="text-muted-foreground">All years (75% of withdrawal for UK phased tax-free)</span>
            </div>

            {hasMainStatePension && (
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-purple-500 rounded"></div>
                <span className="flex-1">{statePensionLabel}</span>
                <span className="text-muted-foreground">
                  Starts age {chartData.find(d => d[statePensionLabel] > 0)?.age || statePensionAge}
                </span>
              </div>
            )}

            {hasSpouseStatePension && (
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-purple-400 rounded"></div>
                <span className="flex-1">{spouseStatePensionLabel}</span>
                <span className="text-muted-foreground">
                  Starts age {chartData.find(d => d[spouseStatePensionLabel] > 0)?.age || 'TBD'}
                </span>
              </div>
            )}

            {hasTaxFreeLumpSum && (
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="flex-1">Tax-Free Lump Sum</span>
                <span className="text-muted-foreground">
                  {country === 'UK' ? 'First year(s) only (25% of pension)' : 'Early years'}
                </span>
              </div>
            )}

            {hasPartTimeWork && (
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-teal-500 rounded"></div>
                <span className="flex-1">Part-Time Work</span>
                <span className="text-muted-foreground">
                  Age {chartData.find(d => d['Part-Time Work'] > 0)?.age || 'N/A'} - {chartData.filter(d => d['Part-Time Work'] > 0).slice(-1)[0]?.age || 'N/A'}
                </span>
              </div>
            )}

            {hasRentalIncome && (
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span className="flex-1">Rental Income</span>
                <span className="text-muted-foreground">Ongoing</span>
              </div>
            )}

            {hasAnnuityIncome && (
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-indigo-500 rounded"></div>
                <span className="flex-1">Annuity Income</span>
                <span className="text-muted-foreground">Per annuity terms</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
